import { Directive, ElementRef, HostListener, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Fixo: DDD + 8 dígitos → (00) 0000-0000.
 * Celular: DDD + 9 dígitos → (00) 0 0000-0000.
 * A máscara do ngx-mask travava em 10 dígitos, então o 9º dígito do celular
 * (o grupo de 5) era descartado antes da máscara trocar.
 */
export function formatBrazilPhone(value: string | null | undefined): string {
  const digits = (value || '').toString().replace(/\D/g, '').slice(0, 11);
  if (!digits) {
    return '';
  }
  if (digits.length > 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length > 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length > 2) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  return `(${digits}`;
}

@Directive({
  selector: '[appMask]'
})
export class MaskDirective implements OnInit, OnDestroy {
  private sub: Subscription | null = null;
  private typing = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  ngOnInit(): void {
    this.applyFormat();
    const control = this.ngControl && this.ngControl.control;
    if (control) {
      this.sub = control.valueChanges.subscribe(() => {
        if (!this.typing) {
          this.applyFormat();
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
      this.sub = null;
    }
  }

  @HostListener('keydown')
  onKeyDown(): void {
    this.typing = true;
  }

  @HostListener('keyup')
  onKeyUp(): void {
    this.typing = false;
  }

  @HostListener('paste')
  onPaste(): void {
    this.typing = true;
  }

  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    const caret = input.selectionStart == null ? (input.value || '').length : input.selectionStart;
    const digitsBefore = (input.value || '').slice(0, caret).replace(/\D/g, '').length;
    const changed = this.writeMasked(formatBrazilPhone(input.value));
    this.typing = false;
    if (!changed) {
      return;
    }
    const placeCaret = () => {
      if (document.activeElement !== input) {
        return;
      }
      const pos = this.positionForDigits(input.value || '', digitsBefore);
      input.setSelectionRange(pos, pos);
    };
    placeCaret();
    setTimeout(placeCaret, 0);
  }

  private applyFormat(): boolean {
    const control = this.ngControl && this.ngControl.control;
    const source = control ? control.value : this.el.nativeElement.value;
    return this.writeMasked(formatBrazilPhone(source));
  }

  private writeMasked(masked: string): boolean {
    const input = this.el.nativeElement;
    const control = this.ngControl && this.ngControl.control;
    const current = control ? (control.value ?? '').toString() : (input.value || '');
    if (current === masked && input.value === masked) {
      return false;
    }
    if (control && current !== masked) {
      control.setValue(masked, { emitEvent: false });
    }
    if (input.value !== masked) {
      input.value = masked;
    }
    return true;
  }

  private positionForDigits(masked: string, digitCount: number): number {
    if (digitCount <= 0) {
      return 0;
    }
    let seen = 0;
    for (let i = 0; i < masked.length; i++) {
      if (/\d/.test(masked.charAt(i))) {
        seen++;
        if (seen === digitCount) {
          return i + 1;
        }
      }
    }
    return masked.length;
  }
}
